import type { CityConfig } from "../city-template/types";

export const florissantMOConfig: CityConfig = {
  city: "Florissant",
  state: "Missouri",
  stateAbbr: "MO",
  slug: "florissant-mo",
  route: "/cities/florissant-mo",
  title: "Emergency Passport Florissant, MO | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Florissant, MO — as fast as 24 hours. Serving North St. Louis County and the greater St. Louis metro. A+ BBB rated registered State Dept. courier serving Florissant, Hazelwood, and Ferguson. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Florissant, MO",
  heroSubtitle: "St. Louis business trip to London with a week to go? Family cruise from Barcelona and the kids need first passports? Last-minute flight out of Lambert Airport and your passport expired? We help Florissant residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Florissant%20Missouri%20North%20St.%20Louis%20County%20suburban%20community%20with%20Old%20St.%20Ferdinand%20Shrine%20historic%20landmark%20tree-lined%20residential%20streets%20community%20parks%20and%20gardens%20midwestern%20charm%20golden%20hour%20soft%20light%20professional%20drone%20photography%20warm%20natural%20tones&width=1800&height=700&seq=florissant-mo-hero-2026&orientation=landscape",
  heroImageAlt: "Florissant Missouri North St. Louis County suburban community with Old St. Ferdinand Shrine at golden hour",
  geoPosition: "38.7892;-90.3226",
  geoPlacename: "Florissant, Missouri",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 Ida B. Wells Drive, 9th Floor, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 300 miles northeast of Florissant via I-55",
  },
  nearbyCitiesZips: [
    { name: "Florissant", zips: "63031, 63033, 63034" },
    { name: "Hazelwood", zips: "63042" },
    { name: "Ferguson", zips: "63135" },
    { name: "St. Louis", zips: "63101–63199" },
    { name: "St. Charles", zips: "63301, 63303" },
    { name: "Bridgeton", zips: "63044" },
    { name: "Maryland Heights", zips: "63043" },
    { name: "Overland", zips: "63114" },
    { name: "Creve Coeur", zips: "63141" },
    { name: "Chesterfield", zips: "63005, 63017" },
    { name: "Spanish Lake", zips: "63138" },
    { name: "Black Jack", zips: "63033" },
    { name: "Dellwood", zips: "63135" },
    { name: "Normandy", zips: "63121" },
    { name: "University City", zips: "63130" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent St. Louis metro departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "North St. Louis County", desc: "Serving Florissant, Hazelwood, Ferguson, and North County communities" },
  ],
  internalLinks: [
    { name: "Expedited passport services in St. Louis, MO", route: "/cities/st-louis" },
    { name: "Fast passport renewal in St. Charles, MO", route: "/cities/st-charles" },
    { name: "Emergency passport help in Columbia, MO", route: "/cities/columbia-mo" },
    { name: "Urgent passport services in O'Fallon, MO", route: "/cities/ofallon-mo" },
    { name: "Same-day passport help in Chicago, IL", route: "/cities/chicago" },
    { name: "Passport courier services in Springfield, IL", route: "/cities/springfield-il" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Florissant remotely via secure courier. We do not have a walk-in office in Florissant — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for North St. Louis County and the St. Louis metro.",
  fpcOfficeAddress: "525 N Tryon St., Charlotte, NC 28202",
  fpcOfficeDistance: "Serving all of North St. Louis County including Florissant, Hazelwood, Ferguson, and surrounding communities",
  serviceAreaIntro: "From the historic Old St. Ferdinand Shrine and the tree-lined streets of Florissant to the business corridors of Hazelwood, from the charming neighborhoods of Ferguson to the industrial parks of Bridgeton, from the community of Black Jack to the suburban feel of Spanish Lake — Fast Passport Center serves the entire North St. Louis County and greater St. Louis metro area. We regularly help travelers from Florissant, Hazelwood, and surrounding communities get their passports fast without the 5-hour drive to Chicago.",
  nearbyCitiesIntro: "We serve all Florissant neighborhoods and the surrounding North St. Louis County area including Hazelwood, Ferguson, Bridgeton, Maryland Heights, Spanish Lake, and Black Jack. If you are anywhere in North St. Louis County or the St. Louis metro, we have you covered.",
  howItWorksIntro: "Most Florissant travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Florissant travelers often face a 5-hour drive to the nearest regional passport agency in Chicago. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Florissant and North St. Louis County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Florissant? Here are the most common ones we hear from North County travelers.",
  statePageRoute: "/states/missouri",
  statePageName: "Missouri",
  dmvWebsite: "https://dor.mo.gov/motor-vehicle/",
  dmvLabel: "Missouri DOR — Motor Vehicle & Driver Licensing",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Florissant travelers with immediate departure needs — as fast as 24 hours for urgent international flights from STL.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "Angela H.",
      location: "Florissant, MO",
      rating: 5,
      text: "Husband surprised me with a Paris anniversary trip but my passport had expired. Fast Passport Center got my renewal in 34 hours. Four days later we were at the Eiffel Tower. Incredible service!",
      service: "Passport Renewal",
    },
    {
      name: "Marcus D.",
      location: "Hazelwood, MO",
      rating: 5,
      text: "Last-minute business trip to Singapore. Realized on Sunday my passport was expired. Called Monday morning, had it by Tuesday afternoon. Flew out Wednesday. North County's best-kept secret!",
      service: "Emergency Passport",
    },
    {
      name: "Patricia S.",
      location: "Ferguson, MO",
      rating: 5,
      text: "Granddaughter's quinceañera in Mexico — both kids needed passports fast. Fast Passport Center got them done in 4 days. The dedicated agent made the whole process so easy. Can't thank them enough!",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "florissant-mo-1",
      name: "Florissant Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1140 N Lafayette St",
      city: "Florissant",
      state: "MO",
      zip: "63031",
      phone: "(314) 839-0310",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Florissant",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Florissant facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "florissant-mo-2",
      name: "St. Louis County Clerk — Clayton",
      type: "County Clerk Passport Acceptance",
      address: "41 S Central Ave, 2nd Floor",
      city: "Clayton",
      state: "MO",
      zip: "63105",
      phone: "(314) 615-7100",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "12 miles south of Florissant",
      website: "https://www.stlouiscountymo.gov",
      websiteLabel: "St. Louis County Clerk",
      notes: "Accepts passport applications at the St. Louis County Government Center. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "florissant-mo-3",
      name: "Hazelwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6133 N Lindbergh Blvd",
      city: "Hazelwood",
      state: "MO",
      zip: "63042",
      phone: "(314) 731-6211",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles west of Florissant",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Hazelwood and northern St. Louis County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "florissant-mo-4",
      name: "Ferguson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 S Florissant Rd",
      city: "Ferguson",
      state: "MO",
      zip: "63135",
      phone: "(314) 522-4462",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles south of Florissant",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Ferguson and south Florissant area. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "florissant-mo-5",
      name: "Bridgeton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11977 St. Charles Rock Rd",
      city: "Bridgeton",
      state: "MO",
      zip: "63044",
      phone: "(314) 291-2542",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles west of Florissant",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Bridgeton and northwest St. Louis County. Full passport services available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Florissant photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at St. Louis County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Florissant applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many North County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Florissant travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Florissant." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Florissant address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Florissant before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Florissant for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5-hour drive to Chicago." },
    { q: "How fast can I renew my passport in Florissant?", a: "Fast Passport Center offers fast passport renewal in Florissant in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Florissant?", a: "The nearest regional passport agency to Florissant is the Chicago Passport Agency, approximately 300 miles northeast via I-55. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Florissant without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Florissant?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 5 hours from Florissant — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for North St. Louis County and St. Louis metro residents." },
    { q: "Do you serve the entire North St. Louis County region?", a: "Absolutely. Fast Passport Center serves the entire North St. Louis County and St. Louis metro region including Florissant, Hazelwood, Ferguson, Bridgeton, and surrounding areas. Our courier service works seamlessly from any address in St. Louis County." },
    { q: "Can I expedite a child passport in Florissant?", a: "Yes. Fast Passport Center expedites child passports in Florissant for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk North County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Florissant with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Florissant", address: "11200 W Florissant Ave, Florissant, MO 63033", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Florissant", address: "12590 New Halls Ferry Rd, Florissant, MO 63033", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Hazelwood", address: "6133 N Lindbergh Blvd, Hazelwood, MO 63042", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Supercenter — Bridgeton", address: "11977 St. Charles Rock Rd, Bridgeton, MO 63044", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Missouri DOR — Bridgeton",
      address: "11977 St. Charles Rock Rd, Bridgeton, MO 63044",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver license and REAL ID services. About 5 minutes from Florissant. Appointment recommended.",
      featured: true,
    },
    {
      name: "Missouri DOR — St. Charles",
      address: "2490 Raymond Dr, St. Charles, MO 63301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves St. Charles County. REAL ID available. Appointment recommended.",
      featured: false,
    },
    {
      name: "Missouri DOR — Maplewood",
      address: "7350 Manchester Rd, Maplewood, MO 63143",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves central St. Louis County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "STL",
      name: "St. Louis Lambert International Airport",
      distance: "6 miles west of Florissant",
      address: "10701 Lambert International Blvd, St. Louis, MO 63145",
      note: "Major international hub with direct flights worldwide. About 10 minutes from Florissant via I-70.",
      flightUrl: "https://www.flystl.com/",
      flightLabel: "STL Airport",
      featured: true,
    },
    {
      code: "SPI",
      name: "Abraham Lincoln Capital Airport",
      distance: "100 miles northeast of Florissant",
      address: "1200 Capital Airport Dr, Springfield, IL 62707",
      note: "Regional airport with direct flights to major hubs. About 1 hour 45 minutes from Florissant via I-55.",
      flightUrl: "https://www.flyspi.com/",
      flightLabel: "SPI Airport",
      featured: false,
    },
    {
      code: "COU",
      name: "Columbia Regional Airport",
      distance: "120 miles west of Florissant",
      address: "11300 Airport Dr, Columbia, MO 65201",
      note: "Regional airport with connections to Denver and Chicago. About 2 hours from Florissant via I-70.",
      flightUrl: "https://www.flycou.com/",
      flightLabel: "COU Airport",
      featured: false,
    },
  ],
};